import React from 'react';
import {injectIntl} from 'react-intl';
import {observer} from 'mobx-react';
import Field from '../Field';
import {helpArticleURL} from '../../constants';

import {
  LoginForm,
  StyledText,
  StyledIcon,
  EmailWrapperDiv,
  StyledButton,
  ErrorNotification,
  StyledErrorWrapper,
  StyledErrorIcon,
  StyledLink,
  getHeight,
  StyledButtonLink
} from './styles';

import {FormattedMessage} from '../FormattedComponents';

import {posthogCapture} from '../../utils/posthog-utils';
import {posthogEvents} from '../../posthog-events';

import messages from './messages';

import ErrorCodes from '../../login-errors';
import PhoneInput from 'components/PhoneInput';
import Spinner from 'components/Spinner';

const PhoneAndPasswordLoginError = ({errorResponse, forgotPasswordLink}) => {
  const {status, data} = errorResponse;

  const renderedErrorConfig = [
    {
      shouldRender: (status === 401 && data === 'invalid_password') || (status === 403 && data === 'user_disabled'),
      message: messages.wrongPhoneOrPassword
    },
    {
      shouldRender: status === 401 && data === ErrorCodes.FIFTH_ATTEMPT,
      message: messages.wrongPhoneNumberOrPasswordFourTimes,
      values: {
        helpArticle: (
          <StyledLink href={helpArticleURL} target="_blank">
            <FormattedMessage {...messages.helpArticle} />
          </StyledLink>
        )
      }
    },
    {
      shouldRender: status === 429 && data === ErrorCodes.TOO_MANY_REQUESTS,
      message: messages.wrongEmailOrPasswordTenTimes,
      values: {
        passwordResetLink: (
          <StyledLink href={forgotPasswordLink} target="_blank">
            <FormattedMessage {...messages.passwordResetLink} />
          </StyledLink>
        )
      }
    },
    {
      shouldRender: status === 429 && data === ErrorCodes.TOO_MANY_CHANGE_REQUESTS,
      message: messages.wrongCurrent10thTime,
      values: {
        passwordResetLink: (
          <StyledLink href={forgotPasswordLink} target="_blank">
            <FormattedMessage {...messages.passwordResetLink} />
          </StyledLink>
        )
      }
    },
    {
      shouldRender: status === 403 && data === ErrorCodes.NOT_ALLOWED_WORKSPACE,
      message: messages.doesNotHaveAccess
    }
  ];

  const renderedError = renderedErrorConfig.find(element => element.shouldRender);
  return renderedError ? (
    <StyledErrorWrapper>
      <StyledErrorIcon name={'warning'} />
      <ErrorNotification>
        <FormattedMessage {...renderedError.message} values={renderedError.values} />
      </ErrorNotification>
    </StyledErrorWrapper>
  ) : null;
};

const PhoneAndPasswordLogin = ({
  intl,
  isMobile,
  form,
  forgotPasswordLink,
  onForgotPasswordClick,

  onSubmit,
  onAllSignInOptionsClick,
  errorResponse,
  showErrorMessage,
  isOnlyOneLoginOption,
  isLoading
}) => {
  const {formatMessage} = intl;

  const posthogEvent = posthogEvents.attemptEmailSignIn;
  const inputStyles = {Input: getHeight()};

  return (
    <LoginForm
      onSubmit={e => {
        e.preventDefault();
        posthogCapture(posthogEvent);

        onSubmit();
      }}
    >
      <EmailWrapperDiv>
        <PhoneInput form={form} field="phoneNumber" hideError placeholder={formatMessage(messages.phoneNumber)} />
      </EmailWrapperDiv>

      {!form.$('phoneNumber').isValid && form.$('phoneNumber').isDirty && (
        <StyledErrorWrapper>
          <StyledErrorIcon name={'warning'} />
          <ErrorNotification>{formatMessage(messages.invalidPhoneNumber)}</ErrorNotification>
        </StyledErrorWrapper>
      )}

      <Field
        placeholder={formatMessage(messages.formField_domainPassword)}
        field="password"
        id="password"
        type="password"
        hideError
        styles={inputStyles}
        form={form}
        onChange={e => form.$('password').sync(e.target.value)}
        withPasswordToggle
      />
      {!isMobile && onForgotPasswordClick && (
        <StyledText alignText={'right'}>
          <StyledButtonLink onClick={onForgotPasswordClick}>
            {formatMessage(messages.forgotYourPassword)}
          </StyledButtonLink>
        </StyledText>
      )}

      {errorResponse && (
        <PhoneAndPasswordLoginError errorResponse={errorResponse} forgotPasswordLink={forgotPasswordLink} />
      )}

      <StyledButton isMobile={isMobile} type="submit" disabled={!form.isValid || isLoading}>
        {formatMessage(messages.signIn)}
        {isLoading && <Spinner size="1rem" />}
      </StyledButton>

      {isMobile && (
        <StyledText isMobile alignText={'right'}>
          <StyledButtonLink onClick={onForgotPasswordClick}>
            {formatMessage(messages.forgotYourPassword)}
          </StyledButtonLink>
        </StyledText>
      )}

      {!isMobile && !isOnlyOneLoginOption && (
        <StyledText>
          <StyledButtonLink onClick={onAllSignInOptionsClick}>
            <StyledIcon name={'arrow-left'} />
            {formatMessage(messages.allSignInOptions)}
          </StyledButtonLink>
        </StyledText>
      )}
    </LoginForm>
  );
};

export default injectIntl(observer(PhoneAndPasswordLogin));
